import { CometError } from '#pplx/frontend/comet/client/errors.js';
import { isCometBrowser, isCometMobileBrowser, isCometOffTheRecordProfile, } from '#pplx/frontend/comet/client/utils.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { getTimeOrigin, reportMetric } from '#pplx/frontend/metrics/browser.js';
/**
 * Create a stub extension for mobile platforms that don't support extensions
 */
export function createStubExtension() {
    return {
        sendMessage: () => Promise.reject(new CometError('MOBILE_BROWSER_ERROR')),
        sendMessageToPort: () => () => { },
        isConnected: () => false,
    };
}
/**
 * Initialize an extension
 * @param opts - The options for the extension
 * @returns The extension
 */
export function initExtension({ onMessage, extensionId, name, }) {
    // Mobile platforms don't support extensions - return stub
    if (isCometMobileBrowser()) {
        return createStubExtension();
    }
    const MIN_RECONNECT_ATTEMPTS_TO_LOG = 1;
    let reconnectTimeout;
    let port;
    let wasErrorWithRuntimeLogged = false;
    let hasLoggedDisconnection = false;
    let hasLoggedSendError = false;
    let reconnectAttempts = 0;
    let isInitialConnection = true;
    const scheduleReconnect = () => {
        if (!isInitialConnection) {
            reconnectAttempts++;
        }
        isInitialConnection = false;
        if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
        }
        reconnectAttempts++;
        reconnectTimeout = setTimeout(connect, 1000);
    };
    const handleDisconnect = () => {
        const lastError = chrome.runtime.lastError;
        port = undefined;
        if (reconnectAttempts > MIN_RECONNECT_ATTEMPTS_TO_LOG &&
            isCometBrowser() &&
            !hasLoggedDisconnection) {
            logger.warn('Disconnected. Attempting to reconnect...', {
                extensionId,
                error: lastError?.message,
            });
            hasLoggedDisconnection = true;
        }
        scheduleReconnect();
    };
    const tryClearPort = () => {
        if (!port)
            return;
        port.onMessage.removeListener(onMessage);
        port.onDisconnect.removeListener(handleDisconnect);
        clearTimeout(reconnectTimeout);
        try {
            port.disconnect();
        }
        catch {
            // Ignore
        }
        port = undefined;
    };
    /**
     * Connect to the extension. If extension is disconnected, it will attempt to reconnect.
     */
    function connect() {
        tryClearPort();
        if (typeof chrome === 'undefined' || !chrome.runtime) {
            if (isCometBrowser()) {
                if (!wasErrorWithRuntimeLogged) {
                    logger.error('Comet browser is missing chrome.runtime', {
                        extensionId,
                    });
                    wasErrorWithRuntimeLogged = true;
                }
                scheduleReconnect();
            }
            return;
        }
        try {
            port = chrome.runtime.connect(extensionId, {
                name,
            });
            port.onMessage.addListener(onMessage);
            port.onDisconnect.addListener(handleDisconnect);
            wasErrorWithRuntimeLogged = false;
            hasLoggedDisconnection = false;
            hasLoggedSendError = false;
            if (reconnectAttempts > MIN_RECONNECT_ATTEMPTS_TO_LOG && isCometBrowser()) {
                logger.info('Extension reconnected successfully', {
                    extensionId,
                    reconnect_attempts: reconnectAttempts,
                });
            }
        }
        catch (err) {
            port = undefined;
            scheduleReconnect();
            logger.error('Failed to connect to extension', { err, extensionId });
        }
    }
    // Only connect to the extension if we're in the Comet browser
    // since random extensions can also declare chrome.runtime
    if (isCometBrowser()) {
        connect();
        reportMetric('web.frontend.comet_extension_connected', {
            extensionId,
            startTime: getTimeOrigin(),
            duration: performance.now(),
        });
    }
    return {
        /**
         * Send a message to the extension
         * @param message - The message to send
         * @returns The response from the extension
         */
        sendMessage: (message) => {
            if (!port &&
                reconnectAttempts > MIN_RECONNECT_ATTEMPTS_TO_LOG &&
                isCometBrowser() &&
                !hasLoggedSendError) {
                logger.warn('Attempting to send message while main port is not connected', {
                    extensionId,
                });
                hasLoggedSendError = true;
            }
            // Send the message to the extension
            return sendMessage(extensionId, message);
        },
        /**
         * Send a message to the extension via the port.
         * @param message - The message to send
         * @returns The response from the extension
         */
        sendMessageToPort: (message) => {
            // Only connect to the extension if we're in the Comet browser
            // since random extensions can also declare chrome.runtime
            if (!isCometBrowser()) {
                return () => { };
            }
            if (!port && reconnectAttempts > MIN_RECONNECT_ATTEMPTS_TO_LOG && !hasLoggedSendError) {
                logger.warn('Attempting to send message to port while main port is not connected', {
                    extensionId,
                });
                hasLoggedSendError = true;
            }
            const ephemeralPort = chrome.runtime.connect(extensionId, {
                name: `${name}_${safeRandomUUID()}`,
            });
            ephemeralPort.postMessage(message);
            return () => {
                try {
                    ephemeralPort.disconnect();
                }
                catch {
                    // Ignore
                }
            };
        },
        /**
         * Check if the extension port is currently connected
         * @returns true if connected, false otherwise
         */
        isConnected: () => {
            return !!port;
        },
    };
}
/**
 * Send a message to the extension
 * @param extensionId - The extension ID
 * @param message - The message to send
 * @returns The response from the extension
 */
async function sendMessage(extensionId, message) {
    return new Promise((resolve, reject) => {
        // Only connect to the extension if we're in the Comet browser
        // since random extensions can also declare chrome.runtime
        // Comet doesn't have extension in INCOGNITO, we should not try to send messages
        if (isCometOffTheRecordProfile()) {
            reject(new CometError('INCOGNITO_BROWSER_ERROR'));
            return;
        }
        if (!isCometBrowser()) {
            reject(new CometError('INVALID_BROWSER_ERROR'));
            return;
        }
        if (typeof chrome === 'undefined' || !chrome.runtime) {
            reject(new CometError('MISSING_RUNTIME_ERROR', {
                details: {
                    extensionId,
                    messageType: typeof message === 'object' && message !== null && 'type' in message
                        ? message.type
                        : undefined,
                    message,
                },
            }));
            return;
        }
        chrome.runtime.sendMessage(extensionId, message, (response) => {
            if (chrome.runtime.lastError) {
                reject(new CometError('RUNTIME_LAST_ERROR', {
                    message: chrome.runtime.lastError.message,
                    cause: chrome.runtime.lastError,
                    details: {
                        extensionId,
                        messageType: typeof message === 'object' && message !== null && 'type' in message
                            ? message.type
                            : undefined,
                        message,
                    },
                }));
                return;
            }
            resolve(response);
        });
    });
}
//# sourceMappingURL=extension.js.map